export const termsContent = {
  lastUpdated: "January 27, 2026",
  sections: [
    {
      id: "acceptance",
      title: "1. Acceptance of Terms",
      content: `
        <p>Welcome to Devolysis. These Terms of Service ("Terms") govern your access to and use of our website, services, and digital products. By accessing or using our services, you agree to be bound by these Terms and our Privacy Policy.</p>
        <p>If you do not agree to these Terms, you may not access or use our services. We reserve the right to modify these Terms at any time, and your continued use of our services constitutes acceptance of any changes.</p>
      `,
    },
    {
      id: "services",
      title: "2. Our Services",
      content: `
        <p>Devolysis provides boutique digital development and consulting services, including:</p>
        <ul>
          <li><strong>Web Platform Development:</strong> Custom website and web application development</li>
          <li><strong>Mobile Application Development:</strong> Native and cross-platform mobile solutions</li>
          <li><strong>AI & Intelligent Automation:</strong> Custom AI solutions and process automation</li>
          <li><strong>Consulting Services:</strong> Technical strategy, architecture design, and digital transformation</li>
        </ul>
        <p>Service specifications, deliverables, timelines, and pricing are detailed in individual project agreements or Statements of Work (SOW).</p>
      `,
    },
    {
      id: "client-responsibilities",
      title: "3. Client Responsibilities",
      content: `
        <p>As a client, you agree to:</p>
        <ul>
          <li><strong>Accurate Information:</strong> Provide accurate, complete, and timely information necessary for project execution</li>
          <li><strong>Access & Resources:</strong> Provide reasonable access to systems, data, and stakeholders as required</li>
          <li><strong>Timely Feedback:</strong> Review deliverables and provide feedback within agreed timeframes</li>
          <li><strong>Payment:</strong> Make payments according to the agreed schedule and terms</li>
          <li><strong>Legal Compliance:</strong> Ensure all content and requirements comply with applicable laws</li>
          <li><strong>Third-Party Services:</strong> Obtain necessary licenses for third-party services, APIs, or content</li>
        </ul>
        <p>Delays caused by failure to meet these responsibilities may impact project timelines and may incur additional fees.</p>
      `,
    },
    {
      id: "payment-terms",
      title: "4. Payment Terms",
      content: `
        <p>Payment terms are specified in each project agreement and typically include:</p>
        <h3>4.1 Pricing and Invoicing</h3>
        <ul>
          <li>Project-based pricing or hourly rates as agreed in the SOW</li>
          <li>Invoices issued according to payment milestones or monthly billing cycles</li>
          <li>All prices are in USD unless otherwise specified</li>
        </ul>

        <h3>4.2 Payment Schedule</h3>
        <ul>
          <li><strong>Deposit:</strong> Typically 30-50% upfront before project commencement</li>
          <li><strong>Milestone Payments:</strong> Invoiced upon completion of defined milestones</li>
          <li><strong>Final Payment:</strong> Due upon project completion and acceptance</li>
          <li><strong>Retainer Services:</strong> Billed monthly in advance</li>
        </ul>

        <h3>4.3 Late Payments</h3>
        <ul>
          <li>Payment due within 14 days of invoice date unless otherwise agreed</li>
          <li>Late payments may incur interest charges of 1.5% per month</li>
          <li>We reserve the right to suspend work for accounts overdue by 30+ days</li>
        </ul>
      `,
    },
    {
      id: "intellectual-property",
      title: "5. Intellectual Property Rights",
      content: `
        <h3>5.1 Client Ownership</h3>
        <p>Upon full payment, you own the custom code, designs, and deliverables created specifically for your project, including:</p>
        <ul>
          <li>Custom application code developed for your project</li>
          <li>Original designs, graphics, and visual elements</li>
          <li>Project-specific documentation and materials</li>
        </ul>

        <h3>5.2 Devolysis Ownership</h3>
        <p>Devolysis retains ownership of:</p>
        <ul>
          <li><strong>Pre-existing IP:</strong> Tools, frameworks, libraries, and methodologies developed prior to or independently of your project</li>
          <li><strong>General Knowledge:</strong> Techniques, skills, and experience gained during the project</li>
          <li><strong>Reusable Components:</strong> Generic code modules and utilities not specific to your business logic</li>
        </ul>

        <h3>5.3 Third-Party Components</h3>
        <p>Projects may incorporate third-party libraries, frameworks, or services subject to their respective licenses. You are responsible for complying with these licenses in your use of the deliverables.</p>

        <h3>5.4 Portfolio Rights</h3>
        <p>Unless otherwise agreed in writing, Devolysis may showcase completed projects in our portfolio, case studies, and marketing materials. We will not disclose confidential business information without permission.</p>
      `,
    },
    {
      id: "confidentiality",
      title: "6. Confidentiality",
      content: `
        <p>Both parties agree to maintain the confidentiality of proprietary information:</p>
        <ul>
          <li><strong>Definition:</strong> Confidential information includes business plans, technical specifications, source code, trade secrets, and any information marked as confidential</li>
          <li><strong>Obligations:</strong> Use confidential information only for project purposes and protect it with reasonable care</li>
          <li><strong>Exceptions:</strong> Information that is publicly available, independently developed, or required to be disclosed by law</li>
          <li><strong>Duration:</strong> Confidentiality obligations continue for 3 years after project completion or as specified in separate NDAs</li>
        </ul>
      `,
    },
    {
      id: "warranties",
      title: "7. Warranties and Disclaimers",
      content: `
        <h3>7.1 Our Warranties</h3>
        <p>Devolysis warrants that:</p>
        <ul>
          <li>Services will be performed in a professional and workmanlike manner</li>
          <li>Deliverables will substantially conform to agreed specifications</li>
          <li>We have the right to provide the services and grant the agreed licenses</li>
          <li>We will use industry-standard security practices to protect client data</li>
        </ul>

        <h3>7.2 Warranty Period</h3>
        <p>Bug fixes and corrections for defects in custom code are provided for 90 days after final delivery at no additional cost. After this period, support is available under separate maintenance agreements.</p>

        <h3>7.3 Disclaimers</h3>
        <p>EXCEPT AS EXPRESSLY PROVIDED, SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT WARRANT:</p>
        <ul>
          <li>Uninterrupted or error-free operation</li>
          <li>Compatibility with all future third-party systems or updates</li>
          <li>Achievement of specific business results or ROI</li>
          <li>Security against all possible threats or vulnerabilities</li>
        </ul>
      `,
    },
    {
      id: "limitation-liability",
      title: "8. Limitation of Liability",
      content: `
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
        <ul>
          <li><strong>Cap on Liability:</strong> Our total liability for any claims arising from services shall not exceed the fees paid for the specific project or service giving rise to the claim</li>
          <li><strong>Excluded Damages:</strong> We are not liable for indirect, consequential, incidental, special, or punitive damages, including lost profits, lost data, or business interruption</li>
          <li><strong>Exceptions:</strong> These limitations do not apply to liability for gross negligence, willful misconduct, or violations of intellectual property rights</li>
        </ul>
        <p>You acknowledge that our fees reflect these limitations and that we would not provide services at these prices without them.</p>
      `,
    },
    {
      id: "termination",
      title: "9. Termination",
      content: `
        <h3>9.1 Termination by Client</h3>
        <p>You may terminate a project with 14 days written notice. Upon termination:</p>
        <ul>
          <li>You remain obligated to pay for all work completed and expenses incurred</li>
          <li>We will deliver all completed work and work-in-progress in its current state</li>
          <li>IP rights transfer only for work that has been paid for in full</li>
        </ul>

        <h3>9.2 Termination by Devolysis</h3>
        <p>We may terminate services if:</p>
        <ul>
          <li>You fail to make payments as agreed (after 15 days notice)</li>
          <li>You materially breach these Terms</li>
          <li>You request work that violates laws or our ethical standards</li>
          <li>The working relationship becomes untenable despite good-faith efforts to resolve issues</li>
        </ul>

        <h3>9.3 Effect of Termination</h3>
        <p>Upon termination, all payment obligations for work performed become immediately due, and both parties must return or destroy confidential information as requested.</p>
      `,
    },
    {
      id: "indemnification",
      title: "10. Indemnification",
      content: `
        <p>You agree to indemnify and hold Devolysis harmless from claims arising from:</p>
        <ul>
          <li>Your use of deliverables in violation of applicable laws</li>
          <li>Content, data, or materials you provide that infringe third-party rights</li>
          <li>Your breach of these Terms</li>
          <li>Modifications you make to deliverables after delivery</li>
        </ul>
        <p>We will indemnify you against claims that our custom work infringes third-party intellectual property rights, provided you notify us promptly and allow us to control the defense.</p>
      `,
    },
    {
      id: "dispute-resolution",
      title: "11. Dispute Resolution",
      content: `
        <p>In the event of disputes:</p>
        <ul>
          <li><strong>Good Faith Negotiation:</strong> Parties agree to attempt resolution through good-faith discussion for 30 days</li>
          <li><strong>Mediation:</strong> If negotiation fails, parties agree to mediation before pursuing litigation</li>
          <li><strong>Arbitration:</strong> Disputes not resolved through mediation shall be settled by binding arbitration</li>
          <li><strong>Governing Law:</strong> These Terms are governed by the laws of [Your Jurisdiction]</li>
          <li><strong>Venue:</strong> Any legal proceedings shall be brought in [Your Jurisdiction] courts</li>
        </ul>
      `,
    },
    {
      id: "general",
      title: "12. General Provisions",
      content: `
        <ul>
          <li><strong>Entire Agreement:</strong> These Terms, together with project-specific agreements, constitute the entire agreement between parties</li>
          <li><strong>Amendments:</strong> Modifications must be in writing and signed by authorized representatives</li>
          <li><strong>Assignment:</strong> You may not assign rights or obligations without our written consent</li>
          <li><strong>Force Majeure:</strong> Neither party is liable for delays caused by events beyond reasonable control</li>
          <li><strong>Severability:</strong> If any provision is unenforceable, the remainder continues in effect</li>
          <li><strong>Waiver:</strong> Failure to enforce a provision does not waive the right to enforce it later</li>
          <li><strong>Independent Contractors:</strong> The parties are independent contractors, not partners or joint venturers</li>
        </ul>
      `,
    },
    {
      id: "contact",
      title: "13. Contact Information",
      content: `
        <p>For questions about these Terms or to discuss project agreements:</p>
        <ul>
          <li><strong>Email:</strong> <a href="mailto:contact@devolysis.com">contact@devolysis.com</a></li>
          <li><strong>Phone:</strong> +1 (307) 487-8301</li>
        </ul>
      `,
    },
  ],
};
